import { Building2, Wrench, Key, UserCheck } from 'lucide-react'
import type { SearchResult } from '../../types/navigation'

interface SearchResultsDropdownProps {
  results: SearchResult[]
  showResults: boolean
  searchQuery: string
  searchLoading: boolean
  onResultClick: (result: SearchResult) => void
  variant?: 'desktop' | 'mobile'
}

function SearchResultIcon({ type }: { type: SearchResult['type'] }) {
  const config = {
    property: { Icon: Building2, bg: 'var(--color-accent-muted)', color: 'var(--color-accent)' },
    issue: { Icon: Wrench, bg: 'var(--color-warning-muted)', color: 'var(--color-warning)' },
    tenancy: { Icon: Key, bg: 'var(--color-success-muted)', color: 'var(--color-success)' },
    prospect: { Icon: UserCheck, bg: 'var(--color-info-muted)', color: 'var(--color-info)' },
  }[type]

  return (
    <div
      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ background: config.bg }}
    >
      <config.Icon className="w-4 h-4" style={{ color: config.color }} />
    </div>
  )
}

export default function SearchResultsDropdown({
  results,
  showResults,
  searchQuery,
  searchLoading,
  onResultClick,
  variant = 'desktop',
}: SearchResultsDropdownProps) {
  if (!showResults) return null

  const isDesktop = variant === 'desktop'

  // Desktop: absolute positioned, anchored right, wider than input to show full results
  // Mobile: static positioned, max-height with scroll
  const containerClasses = isDesktop
    ? 'absolute top-full right-0 mt-2 min-w-80 rounded-xl shadow-lg overflow-hidden z-50'
    : 'mt-2 rounded-xl shadow-lg overflow-hidden max-h-64 overflow-y-auto z-50'

  const noResultsClasses = isDesktop
    ? 'absolute top-full right-0 mt-2 min-w-80 rounded-xl shadow-lg p-4 text-center z-50'
    : 'mt-2 rounded-xl shadow-lg p-4 text-center'

  if (results.length > 0) {
    return (
      <div
        className={containerClasses}
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
      >
        {results.map((result) => (
          <button
            key={`${result.type}-${result.id}`}
            onClick={() => onResultClick(result)}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition${isDesktop ? ' hover:bg-opacity-50' : ''}`}
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: isDesktop ? undefined : '1px solid var(--border-color-light)',
              cursor: 'pointer',
            }}
            onMouseEnter={isDesktop ? (e => e.currentTarget.style.background = 'var(--bg-surface)') : undefined}
            onMouseLeave={isDesktop ? (e => e.currentTarget.style.background = 'transparent') : undefined}
          >
            <SearchResultIcon type={result.type} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{result.title}</p>
              <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{result.subtitle}</p>
            </div>
          </button>
        ))}
      </div>
    )
  }

  if (searchQuery && !searchLoading) {
    return (
      <div
        className={noResultsClasses}
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
      >
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No results found</p>
      </div>
    )
  }

  return null
}
